export const MAX_SENSOR_POLYLINE_POINTS = 1200;
export const MAX_SENSOR_MARKERS = 500;

export interface SensorIdentity {
  sensor_tracking_id?: string;
  id?: string;
  priority?: number;
}

export interface SensorTrackPoint {
  latitude?: number | null;
  longitude?: number | null;
  lat_updated?: string | null;
  shipment_sensor_id?: string | null;
  sensor_tracking_id?: string | null;
  sensor_priority?: number | null;
  _sensorIndex?: number;
}

export interface SensorTrackShipment<TPoint extends SensorTrackPoint> {
  shipment_monitor?: TPoint[] | null;
  monitor_info?: TPoint[] | TPoint | null;
  sensor?: SensorIdentity | null;
  sensors?: SensorIdentity[] | null;
}

export type IndexedSensorTrackPoint<TPoint extends SensorTrackPoint> =
  TPoint & {
    _sensorIndex: number;
  };

export interface SensorSeriesPoint {
  ts: string;
  value: number | boolean;
}

export interface SensorSeriesLike {
  latitude?: SensorSeriesPoint[];
  longitude?: SensorSeriesPoint[];
}

const isFiniteCoordinate = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value !== 0;

export const hasValidSensorCoordinates = (point: SensorTrackPoint): boolean =>
  isFiniteCoordinate(point.latitude) && isFiniteCoordinate(point.longitude);

const SENSOR_POINT_ID_PREFIX = 'sensor:';

/**
 * Identify a reading independently of its position in monitor_info or
 * shipment_monitor. The same Timestream reading can move to a different array
 * offset when the detail page replaces its minimal snapshot with full history.
 */
export const getSensorPointId = (point: SensorTrackPoint): string => {
  const sensorIdentity =
    point.shipment_sensor_id ?? point.sensor_tracking_id ?? 'anonymous';
  const readingIdentity = point.lat_updated ?? [
    point.latitude,
    point.longitude,
  ];

  return `${SENSOR_POINT_ID_PREFIX}${JSON.stringify([
    sensorIdentity,
    readingIdentity,
  ])}`;
};

export const isSensorPointId = (value?: string | null): boolean =>
  Boolean(value?.startsWith(SENSOR_POINT_ID_PREFIX));

export const downsampleEvenly = <T>(items: T[], maxItems: number): T[] => {
  if (items.length <= maxItems) return items;
  if (maxItems <= 1) return [items[items.length - 1]];

  const lastIndex = items.length - 1;
  const result: T[] = [];
  const seen = new Set<number>();

  for (let i = 0; i < maxItems; i += 1) {
    const index = Math.round((i * lastIndex) / (maxItems - 1));
    if (!seen.has(index)) {
      seen.add(index);
      result.push(items[index]);
    }
  }

  return result;
};

export const getPrimarySensorTrackingId = <TPoint extends SensorTrackPoint>(
  shipment: SensorTrackShipment<TPoint>
): string | null => {
  const sensors = shipment.sensors ?? [];
  const primarySensor = [...sensors].sort(
    (a, b) =>
      (a.priority ?? Number.MAX_SAFE_INTEGER) -
      (b.priority ?? Number.MAX_SAFE_INTEGER)
  )[0];

  return (
    (primarySensor?.sensor_tracking_id ||
      primarySensor?.id ||
      shipment.sensor?.id) ??
    shipment.sensor?.sensor_tracking_id ??
    null
  );
};

export const getPrimarySensorPoints = <TPoint extends SensorTrackPoint>(
  shipment: SensorTrackShipment<TPoint>
): TPoint[] => {
  const monitorInfo = shipment.monitor_info;
  const monitorInfoPoints = Array.isArray(monitorInfo)
    ? monitorInfo
    : monitorInfo
      ? [monitorInfo]
      : [];
  const sourcePoints = shipment.shipment_monitor?.length
    ? shipment.shipment_monitor
    : monitorInfoPoints;
  const points = sourcePoints.filter(hasValidSensorCoordinates);
  if (points.length === 0) return [];

  const getPointSensorIdentity = (point: TPoint) =>
    point.sensor_tracking_id || point.shipment_sensor_id || null;
  const distinctSensorIdentities = new Set(
    points
      .map(getPointSensorIdentity)
      .filter((value): value is string => Boolean(value))
  );

  if (distinctSensorIdentities.size <= 1) return points;

  const primaryTrackingId = getPrimarySensorTrackingId(shipment);
  const primaryPoints = primaryTrackingId
    ? points.filter(
        (point) => getPointSensorIdentity(point) === primaryTrackingId
      )
    : [];

  if (primaryPoints.length > 0) return primaryPoints;

  const fallbackIdentity = points
    .map(getPointSensorIdentity)
    .find((identity): identity is string => Boolean(identity));

  return points.filter(
    (point) => getPointSensorIdentity(point) === fallbackIdentity
  );
};

/**
 * Returns the primary sensor's renderable points with an ID that is stable
 * within the source collection. Both the map and its event card must use this
 * function so shipment_monitor and legacy monitor_info responses stay aligned.
 */
export const getIndexedPrimarySensorPoints = <TPoint extends SensorTrackPoint>(
  shipment: SensorTrackShipment<TPoint>
): IndexedSensorTrackPoint<TPoint>[] => {
  const monitorInfo = shipment.monitor_info;
  const monitorInfoPoints = Array.isArray(monitorInfo)
    ? monitorInfo
    : monitorInfo
      ? [monitorInfo]
      : [];
  const indexPoints = (points: TPoint[]) =>
    points.map((point, sourceIndex) => ({
      ...point,
      _sensorIndex: sourceIndex,
    }));
  const selectHistoryPoints = (points: TPoint[]) =>
    getPrimarySensorPoints({
      ...shipment,
      shipment_monitor: indexPoints(points),
      monitor_info: null,
    });

  const historyPoints = selectHistoryPoints(shipment.shipment_monitor ?? []);
  if (historyPoints.length > 0) return historyPoints;

  // monitor_info is a latest-per-sensor snapshot, not a history array. Select
  // one deterministic primary reading rather than drawing unrelated sensors as
  // a trajectory. Preserve its source index so map/card selection still agrees.
  const indexedSnapshots = indexPoints(monitorInfoPoints).filter(
    hasValidSensorCoordinates
  );
  const primarySensor = [...(shipment.sensors ?? [])].sort(
    (a, b) =>
      (a.priority ?? Number.MAX_SAFE_INTEGER) -
      (b.priority ?? Number.MAX_SAFE_INTEGER)
  )[0];
  const primaryTrackingId = getPrimarySensorTrackingId(shipment);
  const primarySnapshot =
    (primarySensor?.id
      ? indexedSnapshots.find(
          (point) => point.shipment_sensor_id === primarySensor.id
        )
      : undefined) ??
    (primaryTrackingId
      ? indexedSnapshots.find(
          (point) => point.sensor_tracking_id === primaryTrackingId
        )
      : undefined) ??
    indexedSnapshots[0];

  return primarySnapshot ? [primarySnapshot] : [];
};

export const getRenderableSensorLayers = <TPoint extends SensorTrackPoint>(
  shipment: SensorTrackShipment<TPoint>
) => {
  const primaryPoints = getPrimarySensorPoints(shipment);

  return {
    primaryPoints,
    polylinePoints: downsampleEvenly(primaryPoints, MAX_SENSOR_POLYLINE_POINTS),
    markerPoints: downsampleEvenly(primaryPoints, MAX_SENSOR_MARKERS),
  };
};

// Ramer-Douglas-Peucker simplification for lat/lng point arrays.
// Removes collinear/redundant points while preserving the shape of the route.
// epsilon is in degrees (0.0001 ≈ 11 m, 0.001 ≈ 111 m).
const perpDistDeg = (
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
): number => {
  const dx = bx - ax;
  const dy = by - ay;
  if (dx === 0 && dy === 0) return Math.hypot(px - ax, py - ay);
  const t = Math.max(
    0,
    Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy))
  );
  return Math.hypot(px - ax - t * dx, py - ay - t * dy);
};

const rdpRecurse = <T extends SensorTrackPoint>(
  items: T[],
  start: number,
  end: number,
  epsilon: number,
  keep: boolean[]
): void => {
  if (end <= start + 1) return;
  const ax = items[start].latitude ?? 0;
  const ay = items[start].longitude ?? 0;
  const bx = items[end].latitude ?? 0;
  const by = items[end].longitude ?? 0;
  let maxDist = 0;
  let maxIdx = start;
  for (let i = start + 1; i < end; i++) {
    const d = perpDistDeg(
      items[i].latitude ?? 0,
      items[i].longitude ?? 0,
      ax,
      ay,
      bx,
      by
    );
    if (d > maxDist) {
      maxDist = d;
      maxIdx = i;
    }
  }
  if (maxDist > epsilon) {
    keep[maxIdx] = true;
    rdpRecurse(items, start, maxIdx, epsilon, keep);
    rdpRecurse(items, maxIdx, end, epsilon, keep);
  }
};

export const rdpSimplify = <T extends SensorTrackPoint>(
  items: T[],
  epsilon: number
): T[] => {
  if (items.length <= 2) return items;
  const keep = new Array<boolean>(items.length).fill(false);
  keep[0] = true;
  keep[items.length - 1] = true;
  rdpRecurse(items, 0, items.length - 1, epsilon, keep);
  return items.filter((_, i) => keep[i]);
};

// Shape-preserving polyline simplification: RDP first (removes collinear points
// without distorting the route), then even-downsample if still above maxPoints.
export const simplifyPolyline = <T extends SensorTrackPoint>(
  items: T[],
  maxPoints: number,
  epsilon = 0.0001
): T[] => {
  const simplified = rdpSimplify(items, epsilon);
  return simplified.length <= maxPoints
    ? simplified
    : downsampleEvenly(simplified, maxPoints);
};

export const buildMapPointsFromSeries = (
  series: SensorSeriesLike | undefined
): SensorTrackPoint[] => {
  if (!series) return [];
  const lats = series.latitude ?? [];
  const lngs = series.longitude ?? [];
  if (!lats.length || !lngs.length) return [];

  const latByTs = new Map<string, number>();
  for (const p of lats) {
    if (typeof p.value === 'number' && Number.isFinite(p.value)) {
      latByTs.set(p.ts, p.value);
    }
  }

  const points: SensorTrackPoint[] = [];
  for (const p of lngs) {
    const lat = latByTs.get(p.ts);
    const lng =
      typeof p.value === 'number' && Number.isFinite(p.value) ? p.value : null;
    if (typeof lat === 'number' && typeof lng === 'number') {
      points.push({ latitude: lat, longitude: lng });
    }
  }
  return points.filter(hasValidSensorCoordinates);
};
